import { BannerProps, PredefinedBanner } from './types'
import { PredefinedBanners } from './PageFlag.constants'
import { useTranslation } from 'next-i18next/pages'
import { PHONE_NOT_REACHABLE, SCHEDULED_DOWNTIME } from 'constants/constants'
import { useStoreSelector } from 'hooks/store'
import { SIMULATION_DOWN } from 'constants/envs'
import { Flag } from '@boltenergy-be/design-system'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import styles from './PageFlag.module.scss'
import classNames from 'classnames'
import { useMemo } from 'react'
import { SOURCE_TYPES } from '@boltenergy-be/shared'

export const PageFlag = ({ onClick }: BannerProps) => {
  // REDUX STORE
  const { banner, bannerType, sourceType } = useStoreSelector((store) => store.marketing)

  // i18n
  const { t } = useTranslation('common')

  // Constants
  const activeScheduledDowntime = SCHEDULED_DOWNTIME.find(({ start, end }) => dayjs().isBetween(start, end))
  const showDefaultBanner = false // set to: pathname === '/' for banner on homepage
  const showCrisisBanner = false
  const showBanner =
    showCrisisBanner || activeScheduledDowntime || banner || bannerType || showDefaultBanner || PHONE_NOT_REACHABLE || SIMULATION_DOWN
  const visibleBanner = useMemo<PredefinedBanner | null>(() => {
    if (activeScheduledDowntime) return PredefinedBanners.scheduledDowntime

    if (SIMULATION_DOWN) return PredefinedBanners.simulationDown

    if (bannerType) return PredefinedBanners[bannerType]

    if (PHONE_NOT_REACHABLE) {
      return {
        type: 'attention',
        text: t('phoneNotReachable')
      }
    }

    if (banner) {
      return {
        type: sourceType === SOURCE_TYPES.push ? 'attention' : 'light',
        text: banner,
        noOnClick: sourceType !== SOURCE_TYPES.push
      }
    }

    if (showCrisisBanner) return PredefinedBanners.middleEastEnergyCrisis

    if (showDefaultBanner) {
      return {
        type: 'attention',
        text: t('defaultBanner')
      }
    }

    return null
  }, [activeScheduledDowntime, banner, bannerType, showCrisisBanner, showDefaultBanner, sourceType, t])

  return (
    showBanner && (
      <div
        data-flag-active={showBanner}
        className={classNames({ [styles.banner]: !visibleBanner?.noOnClick })}
        onClick={!visibleBanner?.noOnClick ? onClick : undefined}
      >
        <Flag
          type={visibleBanner?.type || 'attention'}
          className={classNames({
            [styles['single-line']]: visibleBanner?.singleLine,
            [styles[visibleBanner?.size]]: visibleBanner?.size
          })}
          title={
            visibleBanner?.titleNsAndKey
              ? t(visibleBanner.titleNsAndKey, {
                  ...(activeScheduledDowntime && {
                    count: dayjs(activeScheduledDowntime.start).diff(activeScheduledDowntime.end, 'day') === 0 ? 1 : 2,
                    date: activeScheduledDowntime.start.format('DD/MM/YYYY'),
                    start: activeScheduledDowntime.start.format('DD/MM/YYYY HH:mm'),
                    end: activeScheduledDowntime.end.format('DD/MM/YYYY HH:mm')
                  })
                })
              : visibleBanner?.text
          }
        >
          {!!visibleBanner?.singleLine && ' '}
          {visibleBanner?.descriptionNsAndKey ? (
            <span className={styles.description}>{parse(t(visibleBanner.descriptionNsAndKey))}</span>
          ) : undefined}
        </Flag>
      </div>
    )
  )
}

export default PageFlag
